import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, AlertTriangle, Info } from 'lucide-react'
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  textColumn,
  type ColumnDef,
} from '@/components/ui'
import {
  usePlausibilityFindings,
  type PlausibilityCheckType,
  type PlausibilityFinding,
  type PlausibilitySeverity,
} from '../api/plausibilityApi.ts'

const SEVERITY_LABELS: Record<PlausibilitySeverity, string> = {
  critical: 'Kritisch',
  warning: 'Warnung',
  info: 'Info',
}

const SEVERITY_VARIANT: Record<PlausibilitySeverity, 'danger' | 'warning' | 'info'> = {
  critical: 'danger',
  warning: 'warning',
  info: 'info',
}

const TYPE_LABELS: Record<PlausibilityCheckType, string> = {
  user_count_vs_license: 'Lizenz vs. Nutzer',
  asset_count_vs_position: 'Geräte-Anzahl',
  tier_jump_savings: 'Staffel-Sprung',
  contract_expiring: 'Ablaufender Vertrag',
  missing_party: 'Vertragspartei fehlt',
  zero_quantity: 'Soll-Menge 0',
}

export function PlausibilitySection() {
  const navigate = useNavigate()
  const { data, isLoading, error, refetch } = usePlausibilityFindings()

  const columns = useMemo<ColumnDef<PlausibilityFinding, unknown>[]>(
    () => [
      {
        accessorKey: 'severity',
        header: 'Schwere',
        size: 110,
        cell: ({ getValue }) => {
          const s = getValue() as PlausibilitySeverity
          return (
            <Badge variant={SEVERITY_VARIANT[s]} size="sm">
              {SEVERITY_LABELS[s]}
            </Badge>
          )
        },
      },
      {
        accessorKey: 'type',
        header: 'Typ',
        size: 160,
        cell: ({ getValue }) => TYPE_LABELS[getValue() as PlausibilityCheckType],
      },
      textColumn('contractNumber', 'Vertrag', { size: 130 }),
      {
        accessorKey: 'message',
        header: 'Hinweis',
        cell: ({ row }) => (
          <div>
            <p className="text-small text-gray-900">{row.original.message}</p>
            {row.original.contractName && (
              <p className="text-caption text-gray-500">{row.original.contractName}</p>
            )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 110,
        cell: ({ row }) => (
          <button
            className="text-small text-blue-600 hover:underline"
            onClick={() => navigate(`/vertraege/${row.original.contractId}`)}
          >
            Öffnen →
          </button>
        ),
      },
    ],
    [navigate],
  )

  if (isLoading) return <LoadingState label="Prüfe Verträge …" />
  if (error) {
    return (
      <ErrorState
        description="Plausibilitäts-Checks konnten nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => void refetch()}
      />
    )
  }

  const counts = data?.countsBySeverity ?? { critical: 0, warning: 0, info: 0 }
  const findings = data?.findings ?? []

  return (
    <Card>
      <Card.Header>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-h4 font-semibold text-gray-900">Plausibilitäts-Checks</h3>
            <p className="text-small text-gray-500">
              Konsistenzprüfungen aller aktiven Verträge: Lizenz-/Nutzer-Mengen, Staffel-Sprünge, Ablauffristen, fehlende Vertragspartei.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-small text-red-700">
              <AlertCircle className="w-4 h-4" />
              <strong>{counts.critical}</strong>
            </div>
            <div className="flex items-center gap-1 text-small text-amber-700">
              <AlertTriangle className="w-4 h-4" />
              <strong>{counts.warning}</strong>
            </div>
            <div className="flex items-center gap-1 text-small text-blue-700">
              <Info className="w-4 h-4" />
              <strong>{counts.info}</strong>
            </div>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {findings.length === 0 ? (
          <EmptyState
            title="Alle Verträge plausibel"
            description="Keine offenen Findings — Lizenzen, Mengen, Vertragspartei und Fristen sind konsistent."
          />
        ) : (
          <DataTable columns={columns} data={findings} />
        )}
      </Card.Body>
    </Card>
  )
}
